/**
 * RepConnect - Admin Dashboard for Office Staff
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, Users, Clock, CheckCircle,
  FileText, Settings,
  Bell, Activity, Star, Shield
} from 'lucide-react';
import AdminDelegation from '../utils/adminDelegation';
import { UserStorage } from '../utils/userStorage';

const AdminDashboard = ({ currentUser, setCurrentView, setSelectedMeetingId }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);
  const [physician, setPhysician] = useState(null);

  const loadDashboardData = useCallback(() => {
    if (currentUser && currentUser.physicianId) {
      // Get admin info
      const admin = AdminDelegation.getAdminUser(currentUser.physicianId, currentUser.adminId);
      setAdminInfo(admin);

      // Get physician info
      const physicianData = UserStorage.getUserById(currentUser.physicianId);
      setPhysician(physicianData);

      // Get dashboard data
      const data = AdminDelegation.getAdminDashboardData(currentUser.physicianId);
      setDashboardData(data);

      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const hasPermission = (permission) => {
    return AdminDelegation.hasPermission(adminInfo, permission);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Admin Portal - {physician?.name}
              </h1>
              <p className="text-sm text-gray-600">
                {adminInfo?.title} • {currentUser.name}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-blue-50 px-3 py-1 rounded-full">
                <Shield className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-700 font-medium">
                  {adminInfo?.role?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              </div>
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <Bell className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setCurrentView('admin-settings')}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Meetings</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.totalMeetings}</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Requests</p>
                <p className="text-2xl font-bold text-orange-600">{dashboardData.pendingRequests}</p>
              </div>
              <Clock className="w-8 h-8 text-orange-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Upcoming Meetings</p>
                <p className="text-2xl font-bold text-green-600">{dashboardData.upcomingMeetings}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Reps</p>
                <p className="text-2xl font-bold text-purple-600">12</p>
              </div>
              <Users className="w-8 h-8 text-purple-500" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                <Activity className="w-5 h-5 text-gray-400" />
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {dashboardData.recentActivity.map(activity => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                      activity.priority === 'high' ? 'bg-red-500' :
                      activity.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                    }`}></div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{activity.message}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4">
                {hasPermission(AdminDelegation.PERMISSIONS.MANAGE_MEETINGS) && (
                  <button 
                    onClick={() => setCurrentView('meetings')}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                  >
                    <Calendar className="w-6 h-6 text-blue-600 mb-2" />
                    <p className="font-medium text-gray-900">Manage Calendar</p>
                    <p className="text-sm text-gray-600">View and edit meetings</p>
                  </button>
                )}

                {hasPermission(AdminDelegation.PERMISSIONS.MANAGE_AVAILABILITY) && (
                  <button 
                    onClick={() => setCurrentView('availability')}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                  >
                    <Clock className="w-6 h-6 text-green-600 mb-2" />
                    <p className="font-medium text-gray-900">Set Availability</p>
                    <p className="text-sm text-gray-600">Update schedule</p>
                  </button>
                )}

                {hasPermission(AdminDelegation.PERMISSIONS.MANAGE_PREFERENCES) && (
                  <button 
                    onClick={() => setCurrentView('preferences')}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                  >
                    <Star className="w-6 h-6 text-purple-600 mb-2" />
                    <p className="font-medium text-gray-900">Preferences</p>
                    <p className="text-sm text-gray-600">Dining preferences</p>
                  </button>
                )}

                {hasPermission(AdminDelegation.PERMISSIONS.VIEW_REPORTS) && (
                  <button 
                    onClick={() => setCurrentView('reports')}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                  >
                    <FileText className="w-6 h-6 text-orange-600 mb-2" />
                    <p className="font-medium text-gray-900">Reports</p>
                    <p className="text-sm text-gray-600">View analytics</p>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Permissions Summary */}
        <div className="mt-8 bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Your Permissions</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Object.entries(AdminDelegation.PERMISSIONS).map(([key, permission]) => (
                <div 
                  key={permission}
                  className={`flex items-center space-x-3 p-3 rounded-lg ${
                    hasPermission(permission) ? 'bg-green-50' : 'bg-gray-50'
                  }`}
                >
                  <div className={`w-3 h-3 rounded-full ${
                    hasPermission(permission) ? 'bg-green-500' : 'bg-gray-300'
                  }`}></div>
                  <span className={`text-sm font-medium ${
                    hasPermission(permission) ? 'text-green-900' : 'text-gray-600'
                  }`}>
                    {permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;